import ClientRowModel from '@/models/client/client-row';
import _ from 'lodash';
import dayjs from 'dayjs';
import { GetTaskName } from '@/lib/services/Task';
import { GroupedClientRow } from '@/lib/stores/BoardManipulation/ShiftBoardColumnManipulationStore';
import ClientTaskModel from '@/models/client/client-task';

export interface DependentTaskList {
    firstTask: DependentTaskNode;
}

export interface DependentTaskNode {
    taskId: string;
    rowId: string;
    taskName: string;
    startTime: dayjs.Dayjs;
    endTime: dayjs.Dayjs;
    durationToNextTask: number;
    nextTask: DependentTaskNode | null;
}

export function GenerateDependentTaskList(groupedRows: GroupedClientRow[], identifier: (task: ClientTaskModel)=>Boolean, shiftStart: dayjs.Dayjs, shiftEnd: dayjs.Dayjs): DependentTaskNode | null {
    return GenerateDependentTaskTree(_.flatMap(groupedRows, row => row.headingRow.representsRealLocation ? [row.headingRow, ...row.additionalRows] : row.additionalRows), identifier, shiftStart, shiftEnd);
}

export function GenerateDependentTaskTree(rows: ClientRowModel[], identifier: (task: ClientTaskModel)=>Boolean, shiftStart: dayjs.Dayjs, shiftEnd: dayjs.Dayjs): DependentTaskNode | null {
    let firstTask: DependentTaskNode | null = null;

    let currentNode: DependentTaskNode | null = firstTask;

    // get all the tasks that match the equipment id, and order them by their start time, then by duration
    const validTasks = _.flatMap(rows, row => row.tasks
        .filter(identifier)
        .filter(t => t.startTime.isSameOrAfter(shiftStart) && t.endTime.isSameOrBefore(shiftEnd))
        .map(task=>({
        ...task,
        rowId: row.id,
        rowPriority: row.priority,
    })))
    .sort((a, b) => {
        if (a.startTime.isBefore(b.startTime)) {
            return -1;
        } else if (a.startTime.isAfter(b.startTime)) {
            return 1;
        } else {
            const sortByPriority = a.rowPriority - b.rowPriority;
            return sortByPriority !== 0 ? sortByPriority : a.durationMinutes - b.durationMinutes;
        }
    });
    let previousTaskEndTime: dayjs.Dayjs | null = null;
    // create a linked list of tasks
    for (const task of validTasks) {
        if(currentNode == null){
            currentNode = {
                taskId: task.id,
                rowId: task.rowId,
                taskName: GetTaskName(task),
                startTime: task.startTime,
                endTime: task.endTime,
                durationToNextTask: 0,
                nextTask: null
            };
            firstTask = currentNode;
        } else {
            const nextNode: DependentTaskNode = {
                taskId: task.id,
                rowId: task.rowId,
                taskName: GetTaskName(task),
                startTime: task.startTime,
                endTime: task.endTime,
                durationToNextTask: 0,
                nextTask: null
            };
            currentNode.durationToNextTask = task.startTime.diff(previousTaskEndTime, 'minutes');
            currentNode.nextTask = nextNode;
            currentNode = nextNode;
        }
        previousTaskEndTime = task.endTime;
    }

    return firstTask;
}